
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { RecipientsPage, RecipientsPagedData } from '@routes/campaigns/models/campaign-model';
import { ICampaignMail, ICampaignMailPreview,ICampaignRecipientContact, ICampaignRecipientContacts,
    ICampaignRecipientIds, ICampaignRecipientsExport, ICampaignRecipientsForList, ICampaignRecipientsPrint, ICampaignSenderIds,ICampaignSenders,
    ICampaignSendersForDropdown } from './campaigns-model';

@Injectable()
export class CampaignsService {
    constructor(private http: HttpClient) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${CampaignsService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/campaigns';

    // Mails
    public getMail(id: string, mailId: string): Observable<SuccessResponse<ICampaignMail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ICampaignMail>>(CampaignsService.URL + '/' + id + '/campaignmails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addMail(id: string, mail: ICampaignMail): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const showLogo = JSON.stringify(mail.showLogo);
        const showBanner = JSON.stringify(mail.showBanner);
        const enableBcc = JSON.stringify(mail.enableBcc);
        const subjects = JSON.stringify(mail.subjects);
        const contents = JSON.stringify(mail.contents);
        const formData: FormData = new FormData();

        formData.append('showLogo', showLogo);
        formData.append('showBanner', showBanner);
        formData.append('enableBcc', enableBcc);
        formData.append('subjects', subjects);
        formData.append('contents', contents);
        for (let i = 0; i < mail?.attachements?.length; i++) {
            formData.append('attachements', mail.attachements[i]);
        }

        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/campaignmails', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateMail(id: string, mail: ICampaignMail): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const showLogo = JSON.stringify(mail.showLogo);
        const showBanner = JSON.stringify(mail.showBanner);
        const enableBcc = JSON.stringify(mail.enableBcc);
        const subjects = JSON.stringify(mail.subjects);
        const contents = JSON.stringify(mail.contents);
        const currentAttachements = JSON.stringify(mail.currentAttachements);
        const formData: FormData = new FormData();

        formData.append('showLogo', showLogo);
        formData.append('showBanner', showBanner);
        formData.append('enableBcc', enableBcc);
        formData.append('subjects', subjects);
        formData.append('contents', contents);
        formData.append('currentAttachements', currentAttachements);
        for (let i = 0; i < mail?.attachements?.length; i++) {
            formData.append('attachements', mail.attachements[i]);
        }

        return this.http
            .put<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/campaignmails/' + mail?.id, formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteMail(id: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/campaignmails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewMail(id: string, mailPreview: ICampaignMailPreview): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const showLogo = JSON.stringify(mailPreview.showLogo);
        const showBanner = JSON.stringify(mailPreview.showBanner);
        const subjects = JSON.stringify(mailPreview.subjects);
        const contents = JSON.stringify(mailPreview.contents);
        const languageId = JSON.stringify(mailPreview.languageId);
        const userIds = JSON.stringify(mailPreview.userIds);
        const formData: FormData = new FormData();

        formData.append('showLogo', showLogo);
        formData.append('showBanner', showBanner);
        formData.append('subjects', subjects);
        formData.append('contents', contents);
        formData.append('languageId', languageId);
        formData.append('userIds', userIds);
        for (let i = 0; i < mailPreview?.attachements?.length; i++) {
            formData.append('attachements', mailPreview.attachements[i]);
        }

        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/campaignmails/previews', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    // Senders
    public getSenders(id: string): Observable<SuccessResponse<ICampaignSenders> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ICampaignSenders>>(CampaignsService.URL + '/' + id + '/senders').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addSenders(id: string, senders: ICampaignSenderIds): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/senders', senders).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getSendersForDropdown(id: string): Observable<SuccessResponse<ICampaignSendersForDropdown> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ICampaignSendersForDropdown>>(CampaignsService.URL + '/' + id + '/senders/dropdown').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getSendersForFilter(id: string): Observable<SuccessResponse<ICampaignSendersForDropdown> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ICampaignSendersForDropdown>>(CampaignsService.URL + '/' + id + '/senders/filter').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteSenders(id: string, senderIds: ICampaignSenderIds): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/senders/deletes', senderIds).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    // Recipients
    public getRecipients(id: string, page: RecipientsPage): Observable<SuccessResponse<RecipientsPagedData<ICampaignRecipientsForList>> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<RecipientsPagedData<ICampaignRecipientsForList>>>(CampaignsService.URL + '/' + id + '/recipients', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateRecipients(id: string, recipîents: ICampaignRecipientIds): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/recipients', recipîents).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getRecipientExport(id: string, page: RecipientsPage): Observable<SuccessResponse<ICampaignRecipientsExport> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<ICampaignRecipientsExport>>(CampaignsService.URL + '/' + id + '/recipients/exports', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getRecipientPrint(id: string, page: RecipientsPage): Observable<SuccessResponse<ICampaignRecipientsPrint> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<ICampaignRecipientsPrint>>(CampaignsService.URL + '/' + id + '/recipients/prints', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addRecipientMyself(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/recipients/users', null).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addRecipients(id: string, contacts: ICampaignRecipientContacts): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/recipients/contacts', contacts).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addRecipientNew(id: string, contact: ICampaignRecipientContact): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/recipients/contacts/creates', contact).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteRecipients(id: string, recipients: ICampaignRecipientIds): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(CampaignsService.URL + '/' + id + '/recipients/deletes', recipients).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

}
